import { useSyncExternalStore } from 'react';
import type { LoanDataExportCandidate } from '@/types/loan-data-export';

const STORAGE_KEY = 'lendwise-loan-data-export-selection-v2';
const LEGACY_STORAGE_KEY = 'lendwise-loan-data-export-selection-v1';
const MAX_SELECTION = 1000;

interface SelectionSnapshot {
  ownerKey: string | null;
  candidates: LoanDataExportCandidate[];
}

const EMPTY_SELECTION: LoanDataExportCandidate[] = [];
const SERVER_SNAPSHOT: SelectionSnapshot = { ownerKey: null, candidates: EMPTY_SELECTION };

let candidates: LoanDataExportCandidate[] = [];
let ownerKey: string | null = null;
let snapshot: SelectionSnapshot = SERVER_SNAPSHOT;
let hydrated = false;
const listeners = new Set<() => void>();

// Placeholders carry only a loan ID: they were restored from session storage
// or redacted, and stay hidden until an access check replaces them.
const placeholders = new WeakSet<LoanDataExportCandidate>();

function placeholderCandidate(id: string): LoanDataExportCandidate {
  const candidate: LoanDataExportCandidate = {
    id,
    encompassLoanId: '',
    loanNumber: '',
    borrowerFirstName: null,
    borrowerLastName: null,
    loanFolder: null,
    loanStatus: null,
    loanPurpose: null,
    loanAmount: null,
    propertyAddress: null,
    propertyCity: null,
    propertyState: null,
    loanOfficerName: null
  };
  placeholders.add(candidate);
  return candidate;
}

function publish(): void {
  snapshot = { ownerKey, candidates };
}

function hydrate(): void {
  if (hydrated || typeof window === 'undefined') {
    return;
  }
  hydrated = true;
  try {
    // v1 persisted borrower/property metadata. Remove it rather than carrying
    // sensitive cached rows across the authorization rollout.
    window.sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return;
    }
    const parsed = JSON.parse(stored) as {
      ownerKey?: unknown;
      loanIds?: unknown;
    };
    if (
      typeof parsed.ownerKey === 'string'
      && Array.isArray(parsed.loanIds)
    ) {
      ownerKey = parsed.ownerKey;
      candidates = parsed.loanIds
        .filter((loanId): loanId is string =>
          typeof loanId === 'string' && loanId.length > 0
        )
        .slice(0, MAX_SELECTION)
        .map(placeholderCandidate);
    }
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } finally {
    publish();
  }
}

function persist(): void {
  if (typeof window !== 'undefined') {
    if (!ownerKey) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      ownerKey,
      loanIds: candidates.map((candidate) => candidate.id)
    }));
  }
}

function emit(): void {
  publish();
  persist();
  listeners.forEach((listener) => listener());
}

function getSnapshot(): SelectionSnapshot {
  hydrate();
  return snapshot;
}

function getServerSnapshot(): SelectionSnapshot {
  return SERVER_SNAPSHOT;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function loanDataExportOwnerKey(
  userId: string | null | undefined,
  organizationId: string | null | undefined
): string | null {
  return userId && organizationId ? `${userId}:${organizationId}` : null;
}

export function isLoanDataExportPlaceholder(candidate: LoanDataExportCandidate): boolean {
  return placeholders.has(candidate);
}

/**
 * Adds loans to the shared selection. Callers that collected the loans
 * asynchronously pass the owner they started with, so a response that
 * arrives after a different user signs in adds nothing.
 */
export function addLoanDataExportCandidates(
  nextCandidates: LoanDataExportCandidate[],
  expectedOwnerKey?: string | null
): { added: number; skipped: number; atLimit: boolean } {
  hydrate();
  if (expectedOwnerKey !== undefined && expectedOwnerKey !== ownerKey) {
    return { added: 0, skipped: nextCandidates.length, atLimit: candidates.length >= MAX_SELECTION };
  }
  const byId = new Map(candidates.map((candidate) => [candidate.id, candidate]));
  let added = 0;
  let skipped = 0;
  for (const candidate of nextCandidates) {
    if (!candidate.id || byId.has(candidate.id)) {
      skipped += 1;
      continue;
    }
    if (byId.size >= MAX_SELECTION) {
      skipped += 1;
      continue;
    }
    byId.set(candidate.id, candidate);
    added += 1;
  }
  if (added > 0) {
    candidates = Array.from(byId.values());
    emit();
  }
  return { added, skipped, atLimit: candidates.length >= MAX_SELECTION };
}

export function removeLoanDataExportCandidate(loanId: string): void {
  hydrate();
  const next = candidates.filter((candidate) => candidate.id !== loanId);
  if (next.length !== candidates.length) {
    candidates = next;
    emit();
  }
}

export function clearLoanDataExportCandidates(): void {
  hydrate();
  if (candidates.length > 0) {
    candidates = [];
    emit();
  }
}

export function setLoanDataExportSelectionOwner(
  userId: string | null | undefined,
  organizationId: string | null | undefined
): void {
  hydrate();
  const nextOwnerKey = loanDataExportOwnerKey(userId, organizationId);
  if (ownerKey === nextOwnerKey) {
    return;
  }
  // Loans picked in Loan Center before the export page first claimed the
  // selection have no owner yet; the signed-in user keeps them. The page
  // redacts and re-checks them before showing any. Any other owner change
  // empties the selection.
  const adoptUnowned = ownerKey === null && nextOwnerKey !== null;
  ownerKey = nextOwnerKey;
  if (!adoptUnowned) {
    candidates = [];
  }
  emit();
}

export function redactLoanDataExportCandidates(): void {
  hydrate();
  if (candidates.length === 0) {
    return;
  }
  candidates = candidates.map((candidate) =>
    placeholderCandidate(candidate.id)
  );
  emit();
}

/**
 * Replaces the checked loans with their authorized rows and drops the ones
 * the user can no longer read. Returns how many were dropped so the page can
 * say so; loans added while the check ran are left alone.
 */
export function reconcileLoanDataExportCandidates(
  expectedLoanIds: string[],
  authorizedCandidates: LoanDataExportCandidate[]
): { removed: number } {
  hydrate();
  const expected = new Set(expectedLoanIds);
  const authorized = new Map(
    authorizedCandidates.map((candidate) => [candidate.id, candidate])
  );
  let removed = 0;
  let replacedPlaceholder = false;
  const next = candidates.flatMap((candidate) => {
    if (!expected.has(candidate.id)) {
      return [candidate];
    }
    const resolved = authorized.get(candidate.id);
    if (!resolved) {
      removed += 1;
      return [];
    }
    if (placeholders.has(candidate)) {
      replacedPlaceholder = true;
    }
    return [resolved];
  });
  if (
    removed > 0
    || replacedPlaceholder
    || JSON.stringify(next) !== JSON.stringify(candidates)
  ) {
    candidates = next;
    emit();
  }
  return { removed };
}

/**
 * The shared selection, but only when it belongs to `expectedOwnerKey`.
 * A selection left in memory by a different user never reaches the page.
 */
export function useLoanDataExportSelection(
  expectedOwnerKey: string | null
): LoanDataExportCandidate[] {
  const current = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return expectedOwnerKey !== null && current.ownerKey === expectedOwnerKey
    ? current.candidates
    : EMPTY_SELECTION;
}

export const LOAN_DATA_EXPORT_MAX_SELECTION = MAX_SELECTION;
